import { useEffect, useState } from 'react';
import {
  BarChart3,
  Bus,
  CheckCircle2,
  GitBranch,
  ListChecks,
  PhoneCall,
  PhoneOff,
  TriangleAlert,
  Users,
  XCircle,
  UserRoundSearch,
  CircleDollarSign,
  CircleOff,
  CarFront,
  Gauge,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Badge from '../components/common/Badge';
import Alert from '../components/common/Alert';
import Card from '../components/common/Card';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { ACTIONS, RESOURCES, ROLES } from '../config/accessControl';
import { useAuth } from '../context/AuthContext';
import usePermissions from '../hooks/usePermissions';
import { dashboardService } from '../services';

const generalMetrics = [
  { key: 'totalVoters', label: 'Votantes totales', icon: Users, color: 'bg-primary-50 text-primary-700' },
  { key: 'alreadyVoted', label: 'Ya votaron', icon: CheckCircle2, color: 'bg-success-50 text-success-700' },
  { key: 'coordinators', label: 'Coordinadores', icon: GitBranch, color: 'bg-secondary-50 text-secondary-700' },
  { key: 'leaders', label: 'Punteros', icon: UserRoundSearch, color: 'bg-info-50 text-info-700' },
  { key: 'totalSheets', label: 'Planillas', icon: ListChecks, color: 'bg-slate-100 text-slate-700' },
  { key: 'validSheets', label: 'Planillas válidas', icon: CheckCircle2, color: 'bg-success-50 text-success-700' },
  { key: 'invalidSheets', label: 'Planillas inválidas', icon: XCircle, color: 'bg-danger-50 text-danger-700' },
  { key: 'duplicates', label: 'Duplicados', icon: TriangleAlert, color: 'bg-warning-50 text-warning-700' },
  { key: 'duplicatePhones', label: 'Teléfonos repetidos', icon: PhoneCall, color: 'bg-warning-50 text-warning-700' },
  { key: 'blackVoters', label: 'Votantes verdes', icon: CircleDollarSign, color: 'bg-emerald-600 text-white' },
  { key: 'whiteVoters', label: 'Votantes blancos', icon: CircleOff, color: 'border bg-white text-slate-700' },
  { key: 'votersWithVehicle', label: 'Votantes con móvil', icon: Bus, color: 'bg-success-50 text-success-700' },
  { key: 'votersWithoutVehicle', label: 'Votantes sin móvil', icon: Users, color: 'bg-warning-50 text-warning-700' },
  { key: 'availableVehicles', label: 'Móviles disponibles', icon: CarFront, color: 'bg-primary-50 text-primary-700' },
  { key: 'vehicleOccupancyPercentage', label: 'Ocupación total', icon: Gauge, color: 'bg-info-50 text-info-700', suffix: '%' },
];

const callMetrics = [
  { key: 'totalCalled', label: 'Votantes llamados', suffix: '', icon: PhoneCall },
  { key: 'totalContacted', label: 'Contestaron', suffix: '', icon: CheckCircle2 },
  { key: 'totalNotContacted', label: 'No contestaron', suffix: '', icon: PhoneOff },
  { key: 'globalResponsePercentage', label: 'Respuesta global', suffix: '%', icon: BarChart3 },
  { key: 'averageLeaderResponse', label: 'Promedio por puntero', suffix: '%', icon: Users },
  { key: 'lowResponseSheets', label: 'Planillas con respuesta baja', suffix: '', icon: TriangleAlert },
];

const chartColors = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#64748b', '#db2777', '#65a30d'];
const tooltipStyle = { borderRadius: 10, borderColor: '#e2e8f0', fontSize: 12 };

function ChartEmpty() {
  return <div className="grid h-72 place-items-center text-sm text-content-muted">Sin datos de llamadas para mostrar.</div>;
}

function DashboardPage() {
  const { user, activeCampaign } = useAuth();
  const { can } = usePermissions();
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState('');
  const firstName = user.name.split(' ')[0];

  useEffect(() => {
    let mounted = true;
    setLoadError('');
    dashboardService.getSummary(user, { campaignId: activeCampaign?.id })
      .then((response) => mounted && setSummary(response.data))
      .catch((error) => mounted && setLoadError(error.message));
    return () => { mounted = false; };
  }, [activeCampaign?.id, user]);

  const pendingTasks = summary
    ? [
        { title: 'Validar planillas', detail: `${summary.pending.sheets} pendientes`, resource: RESOURCES.CALL_SHEETS, action: ACTIONS.VALIDATE, roles: [ROLES.ADMINISTRADOR_GENERAL] },
        { title: 'Revisar duplicados', detail: `${summary.pending.duplicates} coincidencias`, resource: RESOURCES.DUPLICATES, action: ACTIONS.RESOLVE },
      ].filter((task) =>
        can(task.resource, task.action) &&
        (!task.roles || task.roles.includes(user.role))
      )
    : [];

  const charts = summary?.charts;

  return (
    <div>
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-sm font-medium text-primary-700">{activeCampaign?.name || 'Campaña activa'}</p>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight text-content-primary">Buenos días, {firstName}</h2>
          <p className="mt-1 text-sm text-content-muted">Este es el resumen actual de tu estructura electoral.</p>
        </div>
        <Badge variant="success" dot>Campaña activa</Badge>
      </div>

      {loadError && <p className="mt-5 rounded-control bg-danger-50 p-4 text-sm text-danger-700">{loadError}</p>}
      {!summary && !loadError && <div className="mt-10"><LoadingSpinner label="Cargando dashboard" /></div>}

      <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {generalMetrics.map(({ key, label, icon: Icon, color, suffix = '' }) => (
          <Card key={key}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-content-muted">{label}</p>
                <p className="mt-2 text-3xl font-semibold tracking-tight text-content-primary">
                  {summary ? `${summary.general[key].toLocaleString('es-PY')}${suffix}` : '—'}
                </p>
              </div>
              <span className={`grid size-10 place-items-center rounded-control ${color}`}><Icon className="size-5" /></span>
            </div>
            {key === 'vehicleOccupancyPercentage' && summary && (
              <p className="mt-3 text-xs text-content-muted">{summary.general.totalOccupancy} de {summary.general.totalVehicleCapacity} lugares</p>
            )}
          </Card>
        ))}
      </div>

      {summary && (summary.warnings?.duplicateDocuments?.length > 0 || summary.warnings?.duplicatePhones?.length > 0) && (
        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          {summary.warnings.duplicateDocuments.length > 0 && (
            <Alert variant="warning" title="Cédulas registradas con más de un puntero">
              {summary.warnings.duplicateDocuments.map((item) => (
                <p key={item.document}>Cédula {item.document}: {item.count} registros</p>
              ))}
            </Alert>
          )}
          {summary.warnings.duplicatePhones.length > 0 && (
            <Alert variant="warning" title="Números de celular repetidos">
              {summary.warnings.duplicatePhones.map((item) => (
                <p key={item.phone}>{item.phone}: utilizado por {item.count} votantes</p>
              ))}
            </Alert>
          )}
        </div>
      )}

      <section className="mt-8">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-content-primary">Control de planillas</h3>
          <p className="text-sm text-content-muted">Métricas calculadas para la campaña y estructura visibles.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {callMetrics.map(({ key, label, suffix, icon: Icon }) => (
            <Card key={key}>
              <div className="flex items-center gap-3">
                <span className="grid size-9 place-items-center rounded-control bg-slate-100 text-content-secondary"><Icon className="size-4" /></span>
                <div><p className="text-sm text-content-muted">{label}</p><p className="text-xl font-semibold text-content-primary">{summary ? `${summary.callControl[key]}${suffix}` : '—'}</p></div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {summary && (
        <section className="mt-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-content-primary">Panorama electoral</h3>
            <p className="text-sm text-content-muted">Distribución de votantes, clasificación y movilización dentro del alcance activo.</p>
          </div>
          <div className="grid min-w-0 gap-5 xl:grid-cols-2">
            <Card title="Votantes por coordinador" description="Registros vinculados a cada coordinación.">
              {charts.votersByCoordinator.length ? (
                <div className="h-72 min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts.votersByCoordinator}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar dataKey="cantidad" name="Votantes" fill="#2563eb" radius={[5, 5, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <ChartEmpty />}
            </Card>

            <Card title="Votantes por puntero" description="Cantidad de votantes por responsable directo.">
              {charts.votersByLeader.length ? (
                <div className="h-72 min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts.votersByLeader} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar dataKey="cantidad" name="Votantes" fill="#7c3aed" radius={[0, 5, 5, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <ChartEmpty />}
            </Card>

            <Card title="Votantes por día" description="Evolución de registros incorporados.">
              {charts.votersByDay.length ? (
                <div className="h-72 min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={charts.votersByDay} margin={{ right: 15 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Line type="monotone" dataKey="cantidad" name="Votantes" stroke="#16a34a" strokeWidth={3} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <ChartEmpty />}
            </Card>

            <Card title="Clasificación" description="Verde genera gasto; Blanco no genera gasto.">
              {charts.classification.some((item) => item.value) ? (
                <div className="h-72 min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={charts.classification} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3} label>
                        <Cell fill="#0f172a" />
                        <Cell fill="#cbd5e1" />
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : <ChartEmpty />}
            </Card>

            <Card title="Estados de votantes" description="Distribución por estado actual del registro.">
              {charts.voterStates.length ? (
                <div className="h-72 min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts.voterStates}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar dataKey="cantidad" name="Votantes" radius={[5, 5, 0, 0]}>
                        {charts.voterStates.map((item, index) => <Cell key={item.name} fill={chartColors[index % chartColors.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <ChartEmpty />}
            </Card>

            <Card title="Ocupación de móviles" description="Lugares utilizados y disponibles, incluyendo acompañantes.">
              {charts.vehicleOccupancy.length ? (
                <div className="h-72 min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts.vehicleOccupancy}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend />
                      <Bar dataKey="utilizada" name="Utilizada" stackId="capacity" fill="#2563eb" />
                      <Bar dataKey="disponible" name="Disponible" stackId="capacity" fill="#cbd5e1" radius={[5, 5, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <ChartEmpty />}
            </Card>
          </div>
        </section>
      )}

      {summary && (
        <section className="mt-6 grid min-w-0 gap-5 xl:grid-cols-2">
          <Card title="Respuesta por coordinador" description="Porcentaje de personas llamadas que contestaron.">
            {charts.coordinatorResponse.length ? (
              <div className="h-72 min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={charts.coordinatorResponse} margin={{ left: 0, right: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(value) => [`${value}%`, 'Respuesta']} />
                    <Bar dataKey="respuesta" fill="#2563eb" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <ChartEmpty />}
          </Card>

          <Card title="Respuesta por puntero" description="Comparación de respuesta dentro de la estructura.">
            {charts.leaderResponse.length ? (
              <div className="h-72 min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={charts.leaderResponse} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={(value) => `${value}%`} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(value) => [`${value}%`, 'Respuesta']} />
                    <Bar dataKey="respuesta" fill="#7c3aed" radius={[0, 5, 5, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <ChartEmpty />}
          </Card>

          <Card title="Resultado de llamadas" description="Distribución de todos los intentos registrados.">
            {charts.callResults.some((item) => item.cantidad) ? (
              <div className="h-72 min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={charts.callResults.filter((item) => item.cantidad)} margin={{ left: 0, right: 10, bottom: 45 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} tick={{ fontSize: 10 }} height={75} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="cantidad" name="Llamadas" radius={[5, 5, 0, 0]}>
                      {charts.callResults.filter((item) => item.cantidad).map((item, index) => <Cell key={item.name} fill={chartColors[index % chartColors.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <ChartEmpty />}
          </Card>

          <Card title="Evolución diaria de llamadas" description="Cantidad de intentos realizados por fecha.">
            {charts.dailyCalls.length ? (
              <div className="h-72 min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={charts.dailyCalls} margin={{ left: 0, right: 15 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={tooltipStyle} labelFormatter={(label) => `Fecha ${label}`} />
                    <Line type="monotone" dataKey="cantidad" name="Llamadas" stroke="#0891b2" strokeWidth={3} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : <ChartEmpty />}
          </Card>

          <Card title="Estado de planillas" description="Pendientes, en proceso, válidas e inválidas.">
            {charts.sheetStates.some((item) => item.value) ? (
              <div className="h-72 min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={charts.sheetStates} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3} label>
                      {charts.sheetStates.map((item, index) => <Cell key={item.name} fill={chartColors[index % chartColors.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : <ChartEmpty />}
          </Card>

          <Card title="Tareas pendientes" description="Acciones disponibles para el rol activo.">
            <div className="space-y-3">
              {pendingTasks.map((task) => (
                <button key={task.title} className="flex w-full items-center justify-between rounded-control border p-3 text-left transition hover:border-primary-200 hover:bg-primary-50/50">
                  <span className="text-sm font-medium text-content-primary">{task.title}</span>
                  <span className="text-xs text-content-muted">{task.detail}</span>
                </button>
              ))}
              {!pendingTasks.length && <p className="rounded-control bg-slate-50 p-4 text-sm text-content-muted">No hay acciones pendientes disponibles para tu rol.</p>}
            </div>
          </Card>
        </section>
      )}
    </div>
  );
}

export default DashboardPage;
